<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .keypad-view {
    height: 100%;
    backdrop-filter: blur(2rem);
    padding: 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 10;
    overflow: hidden;
    position: relative;

    /* Remove all button styling and touch effects */
    background: none;
    border: none;
    cursor: default;
    -webkit-tap-highlight-color: transparent;
    user-select: none;

    &:hover,
    &:focus,
    &:active {
      background: none;
      outline: none;
    }
    background-color: transparent;

    &.with-backdrop::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.3);
      z-index: 20;
    }

    .keypad-center {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      flex: 1;
      width: 100%;
    }

    .action-buttons {
      position: absolute;
      bottom: 32px;
      left: 12px;
      right: 12px;
      display: flex;
      gap: utils.in_rem(12px);
      justify-content: center;
      z-index: 30;
    }
  }
</style>

<script>
  import { userStore } from "../../../../api-stores/user-store";
  import { searchCandidates } from "../../../../api/candidates";
  import CandidateSearchList from "../components/CandidateSearchList.svelte";
  import KeypadUI from "../components/KeypadUI.svelte";
  import { diallerState } from "../diallerState.svelte";
  import Button from "../../../../../../svelte-design-system/components/atoms/Button.svelte";
  import { attendeesState } from "../attendeesState.svelte";
  import { getPhone } from "../utils/phoneUtils.js";

  // Consolidated search state
  let searchState = $state({
    candidates: null,
    isVisible: false,
    currentQuery: "",
  });

  let keypadContainer = $state(null);

  let showContinueButton = $derived.by(() => {
    const digits = diallerState.keypadInputDigits.replace(/\D/g, "");
    return digits.length >= 6;
  });

  // Handle clicks to close candidate list
  const handleViewClick = (e) => {
    if (searchState.isVisible) {
      const candidateList = keypadContainer?.querySelector(".candidates-list");
      if (candidateList && !candidateList.contains(e.target)) {
        searchState.isVisible = false;
      }
    }
  };

  // Handle digit press from keypad
  const handleDigitPress = (digit) => {
    diallerState.keypadInputDigits += digit;
  };

  // Handle candidate search
  const candidateSearch = async () => {
    const searchValue = diallerState.keypadInputDigits;
    searchState.currentQuery = searchValue;

    if (searchValue.length < 1) {
      searchState.isVisible = false;
      searchState.candidates = null;
      return;
    }

    const response = await searchCandidates({
      action: "search",
      value: searchValue,
    });

    // Only update UI if this response matches the current input
    if (searchState.currentQuery === searchValue) {
      searchState.candidates = response.candidates;
      searchState.isVisible = response.candidates?.length > 0;
    }
  };

  // Create call participant objects
  const createCallParticipants = async (candidate, phone) => {
    const user = await userStore.once();
    return [
      {
        candidateId: candidate?.candidateId || null,
        name: candidate?.name || "Unknown Candidate",
        phone,
        photo: candidate?.photo || null,
        jobTitle: candidate?.jobTitle || "",
        company: candidate?.company || "",
        role: "candidate",
      },
      {
        id: user.userId,
        name: user.name,
        photo: user.photo,
        jobTitle: user.role,
        company: user.company,
        role: "user",
      },
    ];
  };

  // Handle candidate selection from search results
  const selectCandidate = (candidate) => {
    goToUserView(candidate);
  };

  // Handle call button click
  const goToUserView = async (candidate) => {
    const phone = getPhone(candidate, searchState.currentQuery);
    const participants = await createCallParticipants(candidate, phone);

    // Reset state
    searchState.isVisible = false;
    searchState.candidates = null;
    attendeesState.attendees = participants;
    diallerState.keypadInputDigits = "";
    diallerState.viewMode = "user";
  };

  const handleContinue = async () => {
    const phoneNumber = diallerState.keypadInputDigits.replace(/\D/g, "");
    if (!phoneNumber) return;

    // Search for candidate by phone number
    const response = await searchCandidates({
      action: "search",
      value: phoneNumber,
    });

    let candidate = response.candidates?.[0];

    if (!candidate) {
      candidate = {
        candidateId: null,
        name: "Unknown Candidate",
        phone: phoneNumber,
        photo: null,
        jobTitle: "",
        company: "",
      };
    }

    goToUserView(candidate);
  };
</script>

<button
  class="keypad-view"
  class:with-backdrop={searchState.isVisible}
  bind:this={keypadContainer}
  onclick={handleViewClick}
>
  <div class="keypad-center">
    <KeypadUI
      bind:value={diallerState.keypadInputDigits}
      placeholder="Enter name or number..."
      onDigitPress={handleDigitPress}
      showInput={true}
      oninput={candidateSearch}
    >
      <CandidateSearchList
        candidates={searchState.candidates}
        isVisible={searchState.isVisible}
        onSelectCandidate={selectCandidate}
        searchValue={diallerState.keypadInputDigits}
      />
    </KeypadUI>
  </div>

  {#if showContinueButton}
    <div class="action-buttons">
      <Button onclick={handleContinue} style="width: 100%;">Continue</Button>
    </div>
  {/if}
</button>
